import React, { useCallback, useEffect } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { RoutesNames } from '@/routes/routes'
import { changeTheme, Theme } from '@interco/inter-ui'
import { BridgeService } from '@/services'
import { useGlobalState } from '@/contexts/global-state'
import * as Actions from '@/contexts/global-state/reducer/actions'
import { Skeletons } from '@/components'
import { WbEnvironments, WbSessionActions } from '@interco/inter-webview-bridge'
import { inApp, platform } from '@/services/bridge'

const isAndroid = (): boolean => platform === WbEnvironments.ANDROID

const Main = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [{ user, appType }, dispatch] = useGlobalState()

  const getUserInfo = useCallback(async () => {
    if (user) return

    const response = await BridgeService.getUserInfo()
    dispatch(
      Actions.setUser({
        user: {
          ...response,
          cpf: response.cpf,
        },
      }),
    )
  }, [dispatch, user])

  const getAppType = useCallback(async () => {
    if (appType) return

    const type = await BridgeService.getSuperAppType()

    if (type === 'PF') {
      changeTheme(Theme.PF)
    } else {
      changeTheme(Theme.PJ)
    }

    dispatch(
      Actions.setAppType({
        appType: type || 'PF',
      }),
    )
  }, [dispatch, appType])

  const getPlatform = useCallback(async () => {
    if (!inApp) return

    if (isAndroid()) {
      dispatch(Actions.setMobilePlatform('Android'))
    } else {
      dispatch(Actions.setMobilePlatform('iPhone'))
    }
  }, [dispatch])

  const redirectFirstPage = useCallback(async () => {
    const { onboardingWasPresented } = await BridgeService.verifyOnboarding()

    if (appType !== undefined && user !== undefined) {
      if (onboardingWasPresented) {
        navigate(RoutesNames.HOME)
      } else {
        navigate(RoutesNames.ONBOARDING_FIRST_PAGE)
      }
    }
  }, [appType, navigate, user])

  const onHeaderBackPress = useCallback(() => {
    if (
      [RoutesNames.ROOT, RoutesNames.HOME, RoutesNames.ONBOARDING_FIRST_PAGE].includes(
        location.pathname,
      )
    ) {
      BridgeService.interWbNavigate.requestGoBack()
      return
    }
    navigate(-1)
  }, [location.pathname, navigate])

  const onMount = useCallback(() => {
    const callStack = async () => {
      await getUserInfo()
      await getAppType()
      await getPlatform()
      await redirectFirstPage()
    }
    // Timeout used because MSW not initialized
    BridgeService.inApp ? callStack() : setTimeout(callStack, 1_000)
  }, [getAppType, getPlatform, getUserInfo, redirectFirstPage])

  useEffect(() => {
    onMount()
  }, [onMount])

  useEffect(() => {
    BridgeService.iWb.addWebviewEventListener(WbSessionActions.GO_BACK, onHeaderBackPress)
  }, [onHeaderBackPress])

  return <Skeletons />
}

export default Main
